import { useState } from "react";
import {
  Film,
  Image as ImageIcon,
  Eye,
  X,
  CheckSquare,
  Square,
  ExternalLink,
} from "lucide-react";

export const MultiImageVideoView = ({ label = "Media Files", value = [] }) => {
  const [previewMedia, setPreviewMedia] = useState(null);

  if (!value || !Array.isArray(value) || value.length === 0) {
    return null;
  }

  // Ensure sorting matches the field layout order
  const sortedValue = [...value].sort(
    (a, b) => (a.index ?? 0) - (b.index ?? 0),
  );

  const formatUnderscoreToSpace = (str) => (str ? str.replace(/_/g, " ") : "");

  return (
    <div className="w-full my-4">
      {/* Header / Section Title */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <label className="text-sm font-semibold text-gray-800 capitalize">
            {formatUnderscoreToSpace(label)}
          </label>
          <p className="text-xs text-gray-500">
            {sortedValue.length} {sortedValue.length === 1 ? "item" : "items"}{" "}
            uploaded
          </p>
        </div>
      </div>

      {/* Main Outer Box */}
      <div className="relative rounded-xl border border-gray-200 bg-gray-50/50 p-4">
        {/* Media Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {sortedValue.map((item, index) => {
            const isVideo = item.type === "video";
            const mediaUrl = typeof item === "string" ? item : item.url;
            const mediaAlt = item.alt || `media-${index}`;
            const isChecked = Boolean(item.checked);

            return (
              <div
                key={index}
                className="group relative flex flex-col rounded-xl overflow-hidden border border-gray-200 bg-white shadow-xs transition hover:shadow-md"
              >
                {/* Media Display Area */}
                <div className="relative aspect-video w-full overflow-hidden bg-gray-900">
                  {isVideo ? (
                    <video
                      src={mediaUrl}
                      className="w-full h-full object-cover"
                      muted
                      preload="metadata"
                    />
                  ) : (
                    <img
                      src={mediaUrl}
                      alt={mediaAlt}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  )}

                  {/* Media Type Badge */}
                  <div className="absolute top-2 left-2 z-10 flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/60 backdrop-blur-xs text-[10px] font-semibold text-white">
                    {isVideo ? (
                      <>
                        <Film className="w-3 h-3 text-indigo-400" />
                        Video
                      </>
                    ) : (
                      <>
                        <ImageIcon className="w-3 h-3 text-emerald-400" />
                        Image
                      </>
                    )}
                  </div>

                  {/* Action Overlay */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center p-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setPreviewMedia({
                            url: mediaUrl,
                            type: isVideo ? "video" : "image",
                            alt: mediaAlt,
                          })
                        }
                        className="p-2 rounded-full bg-white/20 hover:bg-white/40 text-white transition backdrop-blur-xs"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>

                      <a
                        href={mediaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-full bg-white/20 hover:bg-white/40 text-white transition backdrop-blur-xs"
                        title="Open in new tab"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>

                {/* Item Metadata (Selection status & Alt display) */}
                <div className="p-3 flex flex-col gap-2 bg-white border-t border-gray-100">
                  <div className="inline-flex items-center gap-2 select-none">
                    {isChecked ? (
                      <CheckSquare className="w-4 h-4 text-indigo-600" />
                    ) : (
                      <Square className="w-4 h-4 text-gray-400" />
                    )}
                    <span className="text-xs font-medium text-gray-700">
                      {isChecked ? "Featured Item" : "Standard Item"}
                    </span>
                  </div>

                  {item.alt ? (
                    <p
                      className="text-xs text-gray-600 bg-gray-50 px-2.5 py-1.5 rounded-lg border border-gray-100 truncate"
                      title={item.alt}
                    >
                      {item.alt}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400 italic bg-gray-50 px-2.5 py-1.5 rounded-lg border border-gray-100">
                      No description provided
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Media Lightbox Modal */}
      {previewMedia && (
        <div
          className="fixed inset-0 z-[50001] flex items-center justify-center bg-black/80 p-4 backdrop-blur-xs"
          onClick={() => setPreviewMedia(null)}
        >
          <div
            className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-xl bg-gray-900 shadow-2xl flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewMedia(null)}
              className="absolute right-3 top-3 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition"
            >
              <X className="w-5 h-5" />
            </button>
            {previewMedia.type === "video" ? (
              <video
                src={previewMedia.url}
                controls
                autoPlay
                className="max-h-[85vh] max-w-full rounded-lg"
              />
            ) : (
              <img
                src={previewMedia.url}
                alt={previewMedia.alt || "Preview"}
                className="max-h-[85vh] max-w-full object-contain rounded-lg p-2"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
